import { CSSProperties, ReactNode, useState } from 'react'

export interface RadioProps {
    variant?: 'primary' | 'secondary'
    checked?: boolean
    defaultChecked?: boolean
    onChange?: (checked: boolean) => void
    name?: string
    value?: string
    title?: string
    /** Optional element rendered inline after the title (e.g. a status badge). */
    titleAccessory?: ReactNode
    description?: string
    error?: string
    invalid?: boolean
    disabled?: boolean
    style?: CSSProperties
}

export const Radio = ({
    variant = 'primary',
    checked: controlledChecked,
    defaultChecked = false,
    onChange,
    name,
    value,
    title,
    titleAccessory,
    description,
    error,
    invalid = false,
    disabled = false,
    style,
}: RadioProps) => {
    const [internalChecked, setInternalChecked] = useState(defaultChecked)
    const isChecked = controlledChecked !== undefined ? controlledChecked : internalChecked
    const hasError = !!error || !!invalid

    const handleToggle = () => {
        if (disabled) return
        if (controlledChecked === undefined) setInternalChecked(true)
        onChange?.(true)
    }

    const borderClass = hasError
        ? 'border-danger'
        : isChecked
          ? 'border-accent'
          : 'border-border group-hover:border-accent active:border-accent focus-visible:border-accent'
    const bgClass = isChecked ? 'bg-surface' : 'bg-surface group-hover:bg-default-hover active:bg-default'
    const restShadowClass = variant === 'primary' && !isChecked && !hasError ? 'shadow-field' : ''

    return (
        <label className={`group flex items-start gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`} style={style}>
            {/* Control */}
            <button
                type="button"
                role="radio"
                aria-checked={isChecked}
                disabled={disabled}
                onClick={handleToggle}
                className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2 p-0 transition-[border-color,background-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed ${borderClass} ${bgClass} ${restShadowClass}`}
            >
                {isChecked && <span className={`size-1.5 rounded-full ${hasError ? 'bg-danger' : 'bg-accent'}`} />}
            </button>

            {/* Hidden native input for form submission */}
            <input type="radio" name={name} value={value} checked={isChecked} onChange={() => {}} disabled={disabled} className="absolute size-0 opacity-0" />

            {/* Label area */}
            {(title || titleAccessory || description || error) && (
                <div className="flex min-w-0 flex-col">
                    {(title || titleAccessory) && (
                        <span className="flex items-center gap-2">
                            {title && <span className={`text-sm font-medium ${hasError ? 'text-danger' : 'text-foreground'}`}>{title}</span>}
                            {titleAccessory}
                        </span>
                    )}
                    {description && <span className="text-sm font-normal text-foreground-muted">{description}</span>}
                    {error && <span className="mt-0.5 text-sm font-normal text-danger">{error}</span>}
                </div>
            )}
        </label>
    )
}
